import { Interval } from './Plan'
import { Dialog } from '../ui/dialog'
import { ClassValue } from 'classnames/types'
import classNames from 'classnames'
import React, { useState } from 'react'
import { Classes, Drawer } from '@blueprintjs/core'
import { Checkbox } from '../ui/checkbox'
import { money } from '../../util/formatters'
import { Marketplace, Marketplaces } from '../../util/marketplaces'
import { format } from 'date-fns'
import { BillingPlan } from './plans'
import { axiosPost } from '../../util/api'
import { Toasts } from '../toaster/Toast'
import { refreshUser } from '../../actions/user'
import { useDispatch, useSelector } from 'react-redux'
import { Elements } from '@stripe/react-stripe-js'
import { fetchCards } from '../../actions/cards'
import { AddCard } from '../../pages/billing/cards'
import { State } from '../../reducers'
import { stripePromise } from '../../util/stripe_promise'
import { useUser } from '../../hooks/useUser'
import { Button } from '../ui/button'

interface ConfirmationDrawerProps {
    trial_days: number
    trial_end_date: Date
    plan: BillingPlan | null
    setPlan(plan: BillingPlan | null): void
    interval: Interval
}

export const ConfirmationDrawer = ({
    trial_end_date,
    plan,
    setPlan,
    interval,
    trial_days,
}: ConfirmationDrawerProps) => {
    const dispatch = useDispatch()
    const [confirmed, setConfirmed] = useState(false)
    const [subscribing, setSubscribing] = useState(false)
    const { cards, loading } = useSelector((store: State) => store.cards)
    const { user } = useUser()

    if (plan == null) {
        return null
    }

    const price: number = interval === Interval.Monthly ? plan.monthly : plan.yearly

    const onSubscribe = () => {
        setSubscribing(true)
        axiosPost('billing/subscription', {
            plan: plan.id,
            interval: interval == Interval.Monthly ? 'month' : 'year',
        })
            .then(() => {
                setPlan(null)
                setSubscribing(false)
                Toasts.success('Subscription created')
                /** @ts-ignore */
                window.rewardful('convert', { email: user?.email })
                dispatch(refreshUser())
            })
            .catch(() => {
                setSubscribing(false)
            })
    }

    if (loading) {
        return null
    }
    if (cards.length == 0) {
        return (
            <Drawer
                title={`Confirm your ${plan.name} plan subscription`}
                isOpen={true}
                size="30%"
                isCloseButtonShown={true}
                onClose={() => setPlan(null)}
            >
                <div className={classNames('p-4', Classes.DRAWER_BODY)}>
                    Please enter in a credit card to set up your subscription.
                    <CardAdd />
                </div>
            </Drawer>
        )
    }

    return (
        <Drawer
            title={`Confirm your ${plan.name} plan subscription`}
            isOpen={true}
            size="30%"
            isCloseButtonShown={true}
            onClose={() => setPlan(null)}
        >
            <div className={classNames('p-4', Classes.DRAWER_BODY)}>
                <div className="text-center mb-8">
                    <div className="text-xl">
                        {money(price, Marketplaces[Marketplace.UnitedStates])}
                    </div>
                    <div>due {format(trial_end_date, 'MMMM d, yyyy')}</div>
                </div>

                <div className="text-xs">
                    <div className="mb-4">
                        When your {trial_days} day trial expires on{' '}
                        {format(trial_end_date, 'MMMM d, yyyy')} you will be billed{' '}
                        {money(price, Marketplaces[Marketplace.UnitedStates])} per{' '}
                        {interval == Interval.Yearly ? 'year' : 'month'} until you cancel. You can
                        cancel at any time from within the app.
                    </div>

                    <Overages interval={interval} className="mb-4" />

                    <div>
                        If you cancel your subscription and have any pending overages, those will be
                        billed at the time of cancellation, prorated to the remaining time in your
                        billing cycle. For example, if you have $10 in overages at the time of
                        cancellation and you are 10 days in to the 30 day billing period, you will
                        be charged $3.33.
                    </div>
                </div>

                <Checkbox
                    label="I understand the billing and overages terms"
                    className="mt-8 mb-4"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.currentTarget.checked)}
                />

                <Button
                    onClick={onSubscribe}
                    loading={subscribing}
                    disabled={!confirmed || subscribing}
                    intent="success"
                    large
                    text="Confirm Subscription"
                />
            </div>
        </Drawer>
    )
}

interface OveragesProps {
    interval: Interval
    className: ClassValue
}

const Overages = ({ interval, className }: OveragesProps) => {
    if (interval == Interval.Monthly) {
        return (
            <div className={classNames(className)}>
                Any overages incurred will be billed at the start of the next billing cycle. For
                example, if you have $10 in overages for your first month, that would be billed at
                the start of your second month.
            </div>
        )
    } else {
        return (
            <div className={classNames(className)}>
                Even though you are subscribed to a yearly plan, any overages incurred will be
                billed monthly. For example, if your annual subscription started on May 1st and you
                had $10 in overages, you would be billed $10 on June 1st for those overages.
            </div>
        )
    }
}

const CardAdd = ({}) => {
    const dispatch = useDispatch()

    return (
        <Elements stripe={stripePromise}>
            <AddCard
                onSave={() => {
                    dispatch(fetchCards())
                }}
            />
        </Elements>
    )
}
